import type { FC, PropsWithChildren } from 'react';
import { useMemo } from 'react';
import { FALLBACK_SCOPE, ScopeContext } from './ScopeContext';
import type { NursingHome } from './types';

interface StaticScopeProviderProps extends PropsWithChildren {
  nursingHomeId: string | undefined;
  nursingHomeName: string | undefined;
  featureFlags: string[] | undefined;
}

/**
 * Scope provider for apps that never let the user switch scope, such as the
 * family member app, where the nursing home follows the resident.
 *
 * @example
 * <StaticScopeProvider
 *   nursingHomeId={resident.nursing_home_id}
 *   nursingHomeName={resident.nursing_home_name}
 *   featureFlags={resident.feature_flags}
 * >
 */
export const StaticScopeProvider: FC<StaticScopeProviderProps> = ({
  children,
  nursingHomeId,
  nursingHomeName,
  featureFlags,
}) => {
  const value = useMemo(() => {
    const selectedNursingHome: NursingHome | undefined = nursingHomeId
      ? {
          id: nursingHomeId,
          name: nursingHomeName ?? '',
          legalEntityId: '',
          pdfLogoId: '',
          paymentsDisabled: false,
          featureFlags: featureFlags ?? [],
          minPaymentAmountInCents: 0,
          maxPaymentAmountInCents: 0,
          minPaymentAmountInCentsEmployeeInitiated: 0,
          maxPaymentAmountInCentsEmployeeInitiated: 0,
          minResidentThresholdsValue: 0,
          maxResidentThresholdsValue: 0,
        }
      : undefined;

    return { ...FALLBACK_SCOPE, selectedNursingHome };
  }, [nursingHomeId, nursingHomeName, featureFlags]);

  return (
    <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>
  );
};
